/**
 * Timeline Markers & Regions
 * Mark important points (verse, chorus, bridge) and select regions for looping/export
 */
import { useState, useCallback, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './ui/button';
import {
  Flag,
  MapPin,
  Plus,
  Trash2,
  Edit2,
  X,
  ChevronDown,
  Lock,
  Unlock,
  Copy,
  Scissors,
  Download,
  Music,
  Mic,
  Zap,
  Star,
  Tag,
} from 'lucide-react';

// Marker types with colors
export const MARKER_TYPES = {
  INTRO: { id: 'intro', name: 'Intro', color: '#22c55e', icon: Music },
  VERSE: { id: 'verse', name: 'Verse', color: '#3b82f6', icon: Mic },
  CHORUS: { id: 'chorus', name: 'Chorus', color: '#f59e0b', icon: Star },
  BRIDGE: { id: 'bridge', name: 'Bridge', color: '#8b5cf6', icon: Zap },
  OUTRO: { id: 'outro', name: 'Outro', color: '#ef4444', icon: Flag },
  SOLO: { id: 'solo', name: 'Solo', color: '#ec4899', icon: Zap },
  BREAKDOWN: { id: 'breakdown', name: 'Breakdown', color: '#14b8a6', icon: ChevronDown },
  CUSTOM: { id: 'custom', name: 'Custom', color: '#6b7280', icon: Tag },
};

// Create a new marker
export const createMarker = (time, type = 'CUSTOM', name = null) => ({
  id: `marker_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
  time,
  type,
  name: name || MARKER_TYPES[type]?.name || 'Marker',
  color: MARKER_TYPES[type]?.color || '#6b7280',
  locked: false,
});

// Create a new region
export const createRegion = (startTime, endTime, name = 'Region', color = '#3b82f6') => ({
  id: `region_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
  startTime,
  endTime,
  name,
  color,
  locked: false,
});

// Marker component displayed on timeline
export const TimelineMarker = ({
  marker,
  pixelsPerSecond,
  onUpdate,
  onDelete,
  onSelect,
  isSelected,
  timelineWidth,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(marker.name);
  const [isDragging, setIsDragging] = useState(false);
  const markerRef = useRef(null);
  const dragStartX = useRef(0);
  const dragStartTime = useRef(0);

  const MarkerIcon = MARKER_TYPES[marker.type]?.icon || Flag;
  const xPosition = marker.time * pixelsPerSecond;

  // Handle drag start
  const handleMouseDown = useCallback((e) => {
    if (marker.locked || isEditing) return;
    e.stopPropagation();
    setIsDragging(true);
    dragStartX.current = e.clientX;
    dragStartTime.current = marker.time;
    onSelect?.(marker.id);
  }, [marker, isEditing, onSelect]);

  // Handle drag
  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e) => {
      const deltaX = e.clientX - dragStartX.current;
      const deltaTime = deltaX / pixelsPerSecond;
      const newTime = Math.max(0, dragStartTime.current + deltaTime);
      onUpdate?.(marker.id, { time: newTime });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, pixelsPerSecond, marker.id, onUpdate]);

  // Handle name edit
  const handleNameSubmit = () => {
    onUpdate?.(marker.id, { name: editName });
    setIsEditing(false);
  };

  return (
    <div
      ref={markerRef}
      className={`absolute top-0 z-20 cursor-pointer group ${isDragging ? 'cursor-grabbing' : ''}`}
      style={{ left: `${xPosition}px`, transform: 'translateX(-50%)' }}
      onMouseDown={handleMouseDown}
      onDoubleClick={(e) => {
        e.stopPropagation();
        if (!marker.locked) setIsEditing(true);
      }}
      data-testid={`marker-${marker.id}`}
    >
      {/* Marker Flag */}
      <div
        className={`relative flex flex-col items-center ${isSelected ? 'scale-110' : ''} transition-transform`}
      >
        {/* Flag Icon */}
        <div
          className={`w-6 h-6 rounded-full flex items-center justify-center shadow-lg ${
            isSelected ? 'ring-2 ring-white' : ''
          }`}
          style={{ backgroundColor: marker.color }}
        >
          <MarkerIcon className="w-3 h-3 text-white" />
        </div>

        {/* Vertical Line */}
        <div
          className="w-0.5 h-8 opacity-70"
          style={{ backgroundColor: marker.color }}
        />

        {/* Label */}
        <div
          className={`absolute top-8 left-1/2 -translate-x-1/2 whitespace-nowrap px-2 py-0.5 rounded text-[10px] font-medium shadow-lg ${
            isSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
          } transition-opacity`}
          style={{ backgroundColor: marker.color, color: 'white' }}
        >
          {isEditing ? (
            <input
              type="text"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              onBlur={handleNameSubmit}
              onKeyDown={(e) => e.key === 'Enter' && handleNameSubmit()}
              className="bg-transparent outline-none w-20 text-center"
              autoFocus
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <>
              {marker.name}
              {marker.locked && <Lock className="w-2 h-2 ml-1 inline" />}
            </>
          )}
        </div>

        {/* Actions (on hover) */}
        {isSelected && !isEditing && (
          <div className="absolute -top-8 left-1/2 -translate-x-1/2 flex gap-1 bg-zinc-800 rounded-lg p-1 shadow-lg">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setIsEditing(true);
              }}
              className="p-1 hover:bg-zinc-700 rounded"
              title="Edit name"
            >
              <Edit2 className="w-3 h-3 text-zinc-300" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onUpdate?.(marker.id, { locked: !marker.locked });
              }}
              className="p-1 hover:bg-zinc-700 rounded"
              title={marker.locked ? 'Unlock' : 'Lock'}
            >
              {marker.locked ? (
                <Lock className="w-3 h-3 text-amber-400" />
              ) : (
                <Unlock className="w-3 h-3 text-zinc-300" />
              )}
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete?.(marker.id);
              }}
              className="p-1 hover:bg-zinc-700 rounded"
              title="Delete"
            >
              <Trash2 className="w-3 h-3 text-red-400" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Region component for timeline
export const TimelineRegion = ({
  region,
  pixelsPerSecond,
  onUpdate,
  onDelete,
  onSelect,
  isSelected,
  laneHeight = 60,
}) => {
  const [isDragging, setIsDragging] = useState(null); // 'move' | 'start' | 'end'
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(region.name);
  const dragStartX = useRef(0);
  const dragStartTime = useRef({ start: 0, end: 0 });

  const startX = region.startTime * pixelsPerSecond;
  const endX = region.endTime * pixelsPerSecond;
  const width = endX - startX;

  // Handle drag
  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e) => {
      const deltaX = e.clientX - dragStartX.current;
      const deltaTime = deltaX / pixelsPerSecond;

      if (isDragging === 'move') {
        const duration = dragStartTime.current.end - dragStartTime.current.start;
        const newStart = Math.max(0, dragStartTime.current.start + deltaTime);
        onUpdate?.(region.id, {
          startTime: newStart,
          endTime: newStart + duration,
        });
      } else if (isDragging === 'start') {
        const newStart = Math.max(0, Math.min(region.endTime - 0.1, dragStartTime.current.start + deltaTime));
        onUpdate?.(region.id, { startTime: newStart });
      } else if (isDragging === 'end') {
        const newEnd = Math.max(region.startTime + 0.1, dragStartTime.current.end + deltaTime);
        onUpdate?.(region.id, { endTime: newEnd });
      }
    };

    const handleMouseUp = () => setIsDragging(null);

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, pixelsPerSecond, region, onUpdate]);

  const startDrag = (type, e) => {
    if (region.locked) return;
    e.stopPropagation();
    setIsDragging(type);
    dragStartX.current = e.clientX;
    dragStartTime.current = { start: region.startTime, end: region.endTime };
    onSelect?.(region.id);
  };

  return (
    <div
      className={`absolute top-0 h-full pointer-events-auto ${isSelected ? 'z-10' : 'z-5'}`}
      style={{ left: `${startX}px`, width: `${width}px` }}
      data-testid={`region-${region.id}`}
    >
      {/* Region Background */}
      <div
        className={`absolute inset-0 opacity-30 ${isSelected ? 'opacity-40' : ''}`}
        style={{ backgroundColor: region.color }}
        onMouseDown={(e) => startDrag('move', e)}
        onDoubleClick={() => !region.locked && setIsEditing(true)}
      />

      {/* Left Resize Handle */}
      <div
        className="absolute left-0 top-0 bottom-0 w-2 cursor-ew-resize hover:bg-white/30 transition-colors"
        onMouseDown={(e) => startDrag('start', e)}
      />

      {/* Right Resize Handle */}
      <div
        className="absolute right-0 top-0 bottom-0 w-2 cursor-ew-resize hover:bg-white/30 transition-colors"
        onMouseDown={(e) => startDrag('end', e)}
      />

      {/* Region Label */}
      <div
        className={`absolute top-0 left-0 right-0 px-2 py-0.5 text-[10px] font-medium text-white truncate ${
          isSelected ? 'bg-black/50' : 'bg-black/30'
        }`}
        style={{ borderBottom: `2px solid ${region.color}` }}
      >
        {isEditing ? (
          <input
            type="text"
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            onBlur={() => {
              onUpdate?.(region.id, { name: editName });
              setIsEditing(false);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                onUpdate?.(region.id, { name: editName });
                setIsEditing(false);
              }
            }}
            className="bg-transparent outline-none w-full"
            autoFocus
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <>
            {region.name}
            {region.locked && <Lock className="w-2 h-2 ml-1 inline" />}
          </>
        )}
      </div>

      {/* Selection Border */}
      {isSelected && (
        <div
          className="absolute inset-0 border-2 pointer-events-none"
          style={{ borderColor: region.color }}
        />
      )}

      {/* Actions */}
      {isSelected && (
        <div className="absolute -top-7 left-1/2 -translate-x-1/2 flex gap-1 bg-zinc-800 rounded-lg p-1 shadow-lg">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onUpdate?.(region.id, { locked: !region.locked });
            }}
            className="p-1 hover:bg-zinc-700 rounded"
            title={region.locked ? 'Unlock' : 'Lock'}
          >
            {region.locked ? (
              <Lock className="w-3 h-3 text-amber-400" />
            ) : (
              <Unlock className="w-3 h-3 text-zinc-300" />
            )}
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete?.(region.id);
            }}
            className="p-1 hover:bg-zinc-700 rounded"
            title="Delete"
          >
            <Trash2 className="w-3 h-3 text-red-400" />
          </button>
        </div>
      )}
    </div>
  );
};

// Marker Type Picker Dropdown
export const MarkerTypePicker = ({ onSelect, position = { x: 0, y: 0 }, onClose }) => {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      className="fixed bg-zinc-800 border border-zinc-700 rounded-lg shadow-xl z-50 overflow-hidden"
      style={{ left: position.x, top: position.y }}
    >
      <div className="px-3 py-2 text-xs font-medium text-zinc-400 bg-zinc-900/50 border-b border-zinc-700">
        Add Marker
      </div>
      <div className="p-1">
        {Object.entries(MARKER_TYPES).map(([key, type]) => {
          const Icon = type.icon;
          return (
            <button
              key={key}
              onClick={() => {
                onSelect(key);
                onClose();
              }}
              className="w-full flex items-center gap-2 px-3 py-2 hover:bg-zinc-700 rounded text-sm text-left transition-colors"
            >
              <div
                className="w-5 h-5 rounded-full flex items-center justify-center"
                style={{ backgroundColor: type.color }}
              >
                <Icon className="w-3 h-3 text-white" />
              </div>
              <span className="text-zinc-200">{type.name}</span>
            </button>
          );
        })}
      </div>
    </motion.div>
  );
};

// Markers & Regions Panel
export const MarkersRegionsPanel = ({
  markers = [],
  regions = [],
  onAddMarker,
  onUpdateMarker,
  onDeleteMarker,
  onAddRegion,
  onUpdateRegion,
  onDeleteRegion,
  onJumpToTime,
  currentTime = 0,
  totalDuration = 60,
}) => {
  const [showAddMenu, setShowAddMenu] = useState(false);
  const [sortBy, setSortBy] = useState('time'); // 'time' | 'type' | 'name'

  const sortedMarkers = [...markers].sort((a, b) => {
    if (sortBy === 'time') return a.time - b.time;
    if (sortBy === 'type') return a.type.localeCompare(b.type);
    return a.name.localeCompare(b.name);
  });

  const sortedRegions = [...regions].sort((a, b) => a.startTime - b.startTime);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="bg-zinc-900 border border-zinc-700 rounded-lg overflow-hidden" data-testid="markers-panel">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2 bg-zinc-800 border-b border-zinc-700">
        <div className="flex items-center gap-2">
          <Flag className="w-4 h-4 text-orange-400" />
          <span className="text-sm font-medium text-white">Markers & Regions</span>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onAddMarker?.(currentTime, 'CUSTOM')}
            className="h-7 px-2"
            title="Add marker at playhead"
          >
            <Plus className="w-3 h-3 mr-1" />
            Marker
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onAddRegion?.(currentTime, Math.min(currentTime + 10, totalDuration))}
            className="h-7 px-2"
            title="Add region from playhead"
          >
            <Plus className="w-3 h-3 mr-1" />
            Region
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="max-h-[300px] overflow-y-auto">
        {/* Markers Section */}
        {sortedMarkers.length > 0 && (
          <div className="p-2">
            <div className="text-[10px] font-medium text-zinc-500 uppercase tracking-wide mb-2 px-2">
              Markers ({markers.length})
            </div>
            {sortedMarkers.map((marker) => {
              const MarkerIcon = MARKER_TYPES[marker.type]?.icon || Flag;
              return (
                <div
                  key={marker.id}
                  onClick={() => onJumpToTime?.(marker.time)}
                  className="w-full flex items-center gap-2 px-2 py-1.5 hover:bg-zinc-800 rounded text-left group cursor-pointer"
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && onJumpToTime?.(marker.time)}
                >
                  <div
                    className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: marker.color }}
                  >
                    <MarkerIcon className="w-3 h-3 text-white" />
                  </div>
                  <span className="flex-1 text-sm text-zinc-300 truncate">{marker.name}</span>
                  <span className="text-xs text-zinc-500 font-mono">{formatTime(marker.time)}</span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeleteMarker?.(marker.id);
                    }}
                    className="opacity-0 group-hover:opacity-100 p-1 hover:bg-zinc-700 rounded transition-opacity"
                  >
                    <Trash2 className="w-3 h-3 text-red-400" />
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {/* Regions Section */}
        {sortedRegions.length > 0 && (
          <div className="p-2 border-t border-zinc-800">
            <div className="text-[10px] font-medium text-zinc-500 uppercase tracking-wide mb-2 px-2">
              Regions ({regions.length})
            </div>
            {sortedRegions.map((region) => (
              <div
                key={region.id}
                onClick={() => onJumpToTime?.(region.startTime)}
                className="w-full flex items-center gap-2 px-2 py-1.5 hover:bg-zinc-800 rounded text-left group cursor-pointer"
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && onJumpToTime?.(region.startTime)}
              >
                <div
                  className="w-5 h-2 rounded flex-shrink-0"
                  style={{ backgroundColor: region.color }}
                />
                <span className="flex-1 text-sm text-zinc-300 truncate">{region.name}</span>
                <span className="text-xs text-zinc-500 font-mono">
                  {formatTime(region.startTime)} - {formatTime(region.endTime)}
                </span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeleteRegion?.(region.id);
                  }}
                  className="opacity-0 group-hover:opacity-100 p-1 hover:bg-zinc-700 rounded transition-opacity"
                >
                  <Trash2 className="w-3 h-3 text-red-400" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Empty State */}
        {markers.length === 0 && regions.length === 0 && (
          <div className="p-8 text-center">
            <Flag className="w-8 h-8 text-zinc-600 mx-auto mb-2" />
            <p className="text-sm text-zinc-500">No markers or regions</p>
            <p className="text-xs text-zinc-600 mt-1">
              Click the timeline ruler to add markers
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

// Hook for managing markers and regions
export const useMarkersAndRegions = (initialMarkers = [], initialRegions = []) => {
  const [markers, setMarkers] = useState(initialMarkers);
  const [regions, setRegions] = useState(initialRegions);
  const [selectedMarkerId, setSelectedMarkerId] = useState(null);
  const [selectedRegionId, setSelectedRegionId] = useState(null);

  const addMarker = useCallback((time, type = 'CUSTOM', name = null) => {
    const newMarker = createMarker(time, type, name);
    setMarkers(prev => [...prev, newMarker]);
    return newMarker;
  }, []);

  const updateMarker = useCallback((id, updates) => {
    setMarkers(prev => prev.map(m => m.id === id ? { ...m, ...updates } : m));
  }, []);

  const deleteMarker = useCallback((id) => {
    setMarkers(prev => prev.filter(m => m.id !== id));
    if (selectedMarkerId === id) setSelectedMarkerId(null);
  }, [selectedMarkerId]);

  const addRegion = useCallback((startTime, endTime, name = 'Region', color = '#3b82f6') => {
    const newRegion = createRegion(startTime, endTime, name, color);
    setRegions(prev => [...prev, newRegion]);
    return newRegion;
  }, []);

  const updateRegion = useCallback((id, updates) => {
    setRegions(prev => prev.map(r => r.id === id ? { ...r, ...updates } : r));
  }, []);

  const deleteRegion = useCallback((id) => {
    setRegions(prev => prev.filter(r => r.id !== id));
    if (selectedRegionId === id) setSelectedRegionId(null);
  }, [selectedRegionId]);

  const clearAll = useCallback(() => {
    setMarkers([]);
    setRegions([]);
    setSelectedMarkerId(null);
    setSelectedRegionId(null);
  }, []);

  return {
    markers,
    regions,
    selectedMarkerId,
    selectedRegionId,
    setSelectedMarkerId,
    setSelectedRegionId,
    addMarker,
    updateMarker,
    deleteMarker,
    addRegion,
    updateRegion,
    deleteRegion,
    clearAll,
    setMarkers,
    setRegions,
  };
};

export default MarkersRegionsPanel;
